import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CustomButton from '@/components/Button/button';
import checkIcon from '@/assets/MapSection/check-icon.svg';
import mapSvg from '@/assets/MapSection/map.svg';
import Section from '@/components/Section/section';
import { useInView } from '@/hooks/useInView';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
const FEATURES = [
  'View map + list with a swipe',
  'Region View',
  'Estate Details',
];
const FEATURE_CHIP_ANIMATION_MS = 550;
const FEATURE_CHIP_STAGGER_MS = 120;

const REGIONS = [
  {
    label: 'North West',
    bg: '#86A2AD',
    text: color.cream['100'],
  },
  {
    label: 'North East',
    bg: '#A8BECE',
    text: color.charcoal['400'],
  },
  {
    label: 'Yorkshire & The Humber',
    bg: '#D8D2CC',
    text: color.charcoal['400'],
  },
  {
    label: 'East Midlands',
    bg: '#DEB1AF',
    text: color.charcoal['400'],
  },
  {
    label: 'Wales',
    bg: '#D2D28C',
    text: color.charcoal['400'],
  },
  {
    label: 'West Midlands',
    bg: '#CA6C61',
    text: color.cream['100'],
  },
  {
    label: 'East Anglia',
    bg: '#BCA786',
    text: color.cream['100'],
  },
  {
    label: 'South East',
    bg: '#909477',
    text: color.cream['100'],
  },
  {
    label: 'South West',
    bg: '#5B7B6E',
    text: color.cream['100'],
  },
  {
    label: 'Greater London',
    bg: '#3E3E40',
    text: color.cream['100'],
  },
] as const;

interface MapSectionProps {
  onDownloadClick: () => void;
}

export default function MapSection({
  onDownloadClick,
}: MapSectionProps): JSX.Element {
  const [sectionRef, inView] = useInView({
    threshold: 0.25,
    rootMargin: '0px 0px -80px 0px',
    unobserveOnce: true,
  });

  useEffect(() => {
    if (inView) {
      gtag('event', 'section_view', {
        event_category: 'Engagement',
        event_label: 'Map Section',
      });
    }
  }, [inView]);
  const [featureChipsRef, chipsInView] = useInView({
    threshold: 0.35,
    rootMargin: '0px 0px -40px 0px',
    unobserveOnce: true,
  });

  return (
    <Section
      ref={sectionRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: color.grey['20'],
        py: { xs: '48px', md: '64px' },
        px: { xs: '24px', md: '64px' },
      }}
    >
      <Typography
        component="h2"
        sx={{
          ...typography.display,
          color: color.charcoal.default,
          textAlign: 'center',
          lineHeight: 1.1,
          mb: { xs: '20px', md: '28px' },
          maxWidth: '560px',
          fontSize: {
            xs: 'var(--font-size-36, 36px)',
            sm: 'var(--font-size-48, 48px)',
            md: typography.display.fontSize,
          },
        }}
      >
        Explore vineyards on an interactive map
      </Typography>
      <Box
        sx={{
          width: '100%',
          maxWidth: '1100px',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: '24px', md: '36px' },
          mb: { xs: '28px', md: '36px' },
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: '80%', md: '300px' },
            maxWidth: { xs: '100%', md: '430px' },
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: { xs: '6px', md: '10px' },
            order: { xs: 2, md: 'unset' },
            margin: { xs: '0 auto', md: '0' },
            justifyContent: { xs: 'center', md: 'flex-start' },
          }}
        >
          {REGIONS.map((region) => (
            <Box
              key={region.label}
              sx={{
                ...typography.heading4,
                backgroundColor: region.bg,
                color: region.text,
                borderRadius: { xs: '100px', md: '10px' },
                px: { xs: '12px', md: '16px' },
                py: { xs: '6px', md: '10px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                lineHeight: 1.1,
                fontWeight: 350,
                fontSize: {
                  xs: 'var(--font-size-12, 12px)',
                  sm: 'var(--font-size-14, 14px)',
                  md: typography.heading4.fontSize,
                },
                whiteSpace: 'nowrap',
              }}
            >
              {region.label}
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '460px', md: '660px' },
            order: { xs: 1, md: 'unset' },
          }}
        >
          <Box
            component="img"
            src={mapSvg}
            alt="Map of UK and Ireland showing vineyard regions"
            width={803}
            height={824}
            loading="lazy"
            sx={{
              width: '100%',
              height: 'auto',
              aspectRatio: '803 / 824',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      </Box>
      <Typography
        sx={{
          ...typography.heading4,
          color: color.charcoal.default,
          textAlign: 'center',
          fontWeight: 350,
          lineHeight: 1.2,
          maxWidth: '680px',
          mb: '16px',
          fontSize: {
            xs: 'var(--font-size-18, 18px)',
            sm: 'var(--font-size-20, 20px)',
            md: typography.heading4.fontSize,
          },
        }}
      >
        Pan across regions, zoom in on clusters, uncover hidden gems across the
        UK.
      </Typography>
      <Box
        ref={featureChipsRef}
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '8px',
          alignItems: 'center',
          justifyContent: 'center',
          mb: { xs: '20px', md: '24px' },
          '@keyframes slideIn': {
            from: {
              opacity: 0,
              transform: 'translateY(16px)',
            },
            to: {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        {FEATURES.map((feature, index) => (
          <Box
            key={feature}
            sx={{
              ...typography.bodyText,
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '6px',
              px: '10px',
              py: '6px',
              borderRadius: '100px',
              backgroundColor: color.terracotta['100'],
              lineHeight: 1,
              color: color.charcoal.default,
              opacity: 0,
              ...(chipsInView && {
                animation: `slideIn ${FEATURE_CHIP_ANIMATION_MS}ms ease-out forwards`,
                animationDelay: `${index * FEATURE_CHIP_STAGGER_MS}ms`,
              }),
              fontSize: {
                xs: 'var(--font-size-16, 16px)',
                sm: 'var(--font-size-18, 18px)',
                md: typography.bodyText.fontSize,
              },
            }}
          >
            <Box
              component="img"
              src={checkIcon}
              alt=""
              aria-hidden
              sx={{
                width: 16,
                height: 16,
                display: 'block',
              }}
            />
            {feature}
          </Box>
        ))}
      </Box>
      <CustomButton
        variant="contained"
        onClick={() => {
          gtag('event', 'cta_click', {
            event_category: 'Engagement',
            event_label: 'Download App – Map Section',
          });
          onDownloadClick();
        }}
      >
        Download App
      </CustomButton>
    </Section>
  );
}
